/**
 * Partner Terms — DRAFT prepared for lawyer review (research + open items in docs/legal/README.md).
 * B2B, but the unfair-contract-terms regime still applies (small-business standard-form contracts),
 * so one-sided clauses are avoided by design. The adviser-vs-accountant licensing split is the
 * clause a lawyer MUST settle: Part B (accountants under the owner's licensing arrangements) needs
 * a proper authorised-representative agreement, not just these software terms.
 * `version` must match server/src/legal/legalVersions.ts — bump both on material change.
 */
export const PARTNER_TERMS = {
  key: 'partner_terms',
  version: '2026-07-09',
  title: 'Partner Terms — Professional Firms',
  effectiveDate: '9 July 2026',
  draft: true,
  body: `
## 1. What these terms cover

These terms are an agreement between **[ENTITY NAME + ABN — to be completed]** ("AgedCareWise",
"we") and the professional firm named at sign-up ("the firm", "you"). They licence your firm to
use the AgedCareWise platform to model and prepare aged-care and retirement-living advice for
**your firm's own clients**. The person accepting these terms confirms they are authorised to
bind the firm.

## 2. Your plan and fees (all prices exclude GST)

- **Annual practice plan:** $800 per year, including up to 20 activated clients, then $70 per
  additional activated client, prompted at the time you add each one.
- **Pay per client:** $336 per activated client, charged when you take each client on. No annual
  commitment; you may switch to the annual plan at any time.
- An "activated client" is a client file created in your workspace (entered by you or invited by
  link). Deleting a file does not refund or free the slot — pricing is per client taken on.
- Fees are payable by card via Stripe. **Your clients never pay AgedCareWise** — how you charge
  your clients for your advice service is entirely your affair, outside the platform.

## 3. Who is responsible for the advice — the critical clause

**Part A — firms holding their own AFSL (financial advisers).**
The platform is a software tool. All personal advice produced with it for your clients is
provided by **your firm under your own AFSL**. You are responsible for: the best-interests duty
and appropriateness of the advice; reviewing every Statement of Advice before it is given; your
own FSG and disclosure documents; and your own record-keeping obligations (the platform's
export and retention features assist, but the obligation is yours).

**Part B — professionals who are not licensed to give financial advice** (accountants, solicitors,
aged-care and Support at Home providers, superannuation trustees and others).

**All personal advice produced with the platform for your clients is provided by AgedCareWise under
our own AFSL. It is not your advice.** You are not our authorised representative, and nothing in
these terms appoints you as one.

You may: introduce clients to the platform; collect and enter their information; and help them
understand what the outputs mean, in the way you would explain any financial document to a client.

**You must not hold yourself out as the provider of the advice**, or as licensed to give it, or as
acting under our licence. You must not recommend a strategy to a client as your own advice. Where a
client asks which option they should take, that is a question for AgedCareWise as the advice
provider.

Your clients are told who provides the advice when they first enter the platform, and every
Statement of Advice names AgedCareWise and our AFSL.

**[WORDING TO BE SETTLED WITH LAWYER. The structure above is the owner's settled position and
replaces an earlier draft that described this as an authorised-representative relationship — it is
not one, and never was. What still needs legal settlement is the drafting: the precise boundary
between explaining an output and providing advice, how that is expressed to the professional, and
what the platform should do where it is crossed.]**

## 4. Your clients' information

- Your workspace and your clients' files are yours to direct. We host and protect them (Privacy
  Policy applies; data stored in Australia, encrypted, strict tenant isolation — your firm can
  only ever see its own clients, and other firms can never see yours).
- You warrant that you have the authority and consents needed to enter your clients' personal
  information into the platform, and that you will tell them how it is handled (our Collection
  Notice text is available for your engagement documents).
- You can export client files at any time. If your plan lapses, your workspace becomes read-only
  with export available for **90 days**, and records the law requires to be retained (Statements
  of Advice) are retained per the Privacy Policy. We will cooperate with your record-keeping
  obligations after exit.
- If either of us suffers an eligible data breach involving your clients' information, we will
  notify the other promptly and cooperate on Notifiable Data Breaches obligations.

## 5. Fair use and restrictions

Seats are per named login; invite colleagues rather than sharing credentials (multi-factor
authentication is mandatory for firm logins). You may not resell, white-label or provide platform
access to third parties, or represent the platform's output as your own software. The AWise
assistant has a fair-use daily allowance per user.

## 6. Service, support and availability

We provide the platform with due care and skill and keep the modelling aligned to current
Australian rules. We aim for high availability but do not promise uninterrupted service; planned
maintenance will be scheduled considerately. We will not make changes that materially degrade the
service you have paid for without the notice in clause 8.

## 7. Liability (business terms)

Nothing in these terms excludes rights that cannot lawfully be excluded. Subject to that: our
liability for a failure of the service is limited to resupplying the service or paying the cost
of resupply; neither of us is liable to the other for indirect or consequential loss; and your
liability for your clients' advice outcomes sits where clause 3 puts it. **[LAWYER TO SETTLE —
including the cap's interaction with the ASIC Act implied warranties.]**

## 8. Changes

We may update these terms with at least **30 days' written notice** for material changes, taking
effect at your next renewal (annual plan) or 30 days after notice (pay-per-client). If a material
change disadvantages your firm, you may terminate before it takes effect and receive a pro-rata
refund of any unused annual fee. We will ask an authorised person to accept updated terms in the
portal.

## 9. Term and termination

Pay-per-client arrangements continue until you stop adding clients or either of us ends the
arrangement on notice. Annual plans run for 12 months and do not auto-renew without an authorised
person confirming renewal. Either party may terminate for material breach that is not remedied
within 14 days of notice. Clauses about client data, records, liability and disputes survive.

## 10. Complaints, disputes, law

Talk to us first at **[SUPPORT EMAIL — to be completed]**. These terms are governed by the laws
of **[STATE — to be completed]**, Australia.
`,
};

export default PARTNER_TERMS;
